import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Ref, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, Keyboard } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { scrollTo } from '@/components/hooks/hash';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { PartsItem } from '@/components/sections/service/part-detail/part-item';
import { Text } from '@/components/shared/element/text';
import { HeadingLevel2 } from '@/components/shared/headline/heading-level-2';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { config, styled } from '@/stitches.config';
import { IButton } from '@/types/shared';
import { IPartDetailGroup } from '@/types/shared/parts';

const SlideContainer = styled(Box, {
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    bottom: '$space-0',
    left: '$space-0',
    width: '100%',
    height: '$size-px',
    pointerEvents: 'none',
    backgroundColor: '$gray300',
  },
  '& .swiper-slide': {
    position: 'relative',
    width: 'auto',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      bottom: '$space-0',
      left: '$space-0',
      width: '100%',
      height: '$size-0-5',
      pointerEvents: 'none',
      backgroundColor: '$gray400',
      zIndex: '2',
      transition:
        'background-color var(--transition-duration) var(--transition-easing)',
    },
    '@hover': {
      '&:hover': {
        '& *': {
          color: '$primary',
        },
      },
    },
    '&.active': {
      '&::before': {
        backgroundColor: '$primary',
      },
      '& *': {
        color: '$primary',
      },
    },
  },
});

export interface RecommendedPartsProps {
  cardUrl?: string;
  groups?: IPartDetailGroup[];
  lead?: string;
  title?: string;
  description?: string;
  buttons?: IButton[];
  dataTrack?: string;
  dataTest?: string;
  dataTestSection?: string;
}

export const RecommendedParts = ({
  cardUrl,
  groups = [],
  lead,
  title,
  description,
  buttons = [],
  dataTrack = 'recommended-parts',
  dataTest = 'recommendedParts',
  dataTestSection = 'section_recommendedParts',
}: RecommendedPartsProps) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
  const [mainSwiper, setMainSwiper] = useState<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isCentered, setIsCentered] = useState<boolean>(false);

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isDesktop = useMediaQuery(config.media.md);

  const paramsMap = useMemo(
    () => groups.map((category) => category.url),
    [groups]
  );

  const updateUrl = (index: number) => {
    const params = paramsMap[index] || '';
    const updateSearchParams = new URLSearchParams(
      searchParams?.toString() || ''
    );
    updateSearchParams.delete('path');
    updateSearchParams.set('products', params);

    if (pathname) {
      router.push(`${pathname}?${updateSearchParams.toString()}`, {
        scroll: false,
      });
    }
  };

  const onMainSwiperChange = (swiper: SwiperClass) => {
    if (!swiper) return;

    setActiveIndex(swiper.activeIndex);

    if (isDesktop) {
      setIsCentered(false);
    } else {
      setIsCentered(true);
    }

    updateUrl(swiper.activeIndex);
  };

  useEffect(() => {
    if (!mainSwiper) return;

    const productParam = searchParams?.get('products') ?? '';
    const index = paramsMap.indexOf(productParam);

    if (productParam && index >= 0) {
      mainSwiper.slideTo(index, 0);
      setActiveIndex(index);
      if (isDesktop) {
        setIsCentered(false);
      } else {
        setIsCentered(true);
      }
      if (sectionRef.current) {
        scrollTo(sectionRef.current);
      }
    } else {
      setActiveIndex(0);
      setIsCentered(false);
    }
  }, [paramsMap, mainSwiper, searchParams, isDesktop]);

  return (
    <Section
      size="2xl"
      backgroundColor="gradient-gray-light-invert"
      ref={sectionRef as Ref<HTMLDivElement>}
      test={dataTestSection}
    >
      <HeadingLevel2
        lead={lead || ''}
        title={title || ''}
        description={description || ''}
        buttons={buttons}
        layout="two-row"
        dataTrack={dataTrack}
        dataTrackSection="recommended-parts"
      />
      {groups?.length > 0 && (
        <>
          <Box
            test={{
              dataTest: `${dataTest}_tab`,
            }}
          >
            <Container size="desktop-only">
              <SlideContainer
                position="relative"
                marginBottom={{ '@initial': '6', '@lg': '10' }}
              >
                <Swiper
                  modules={[Controller]}
                  speed={1200}
                  slidesPerView="auto"
                  spaceBetween={0}
                  touchReleaseOnEdges={true}
                  centeredSlides={isCentered}
                  centeredSlidesBounds={isCentered}
                  watchOverflow={true}
                  controller={{ control: mainSwiper }}
                  onSwiper={setThumbsSwiper}
                  breakpoints={{
                    '768': {
                      centeredSlides: false,
                      centeredSlidesBounds: false,
                    },
                  }}
                >
                  {groups.map((o, i) => (
                    <SwiperSlide
                      key={i}
                      className={activeIndex === i ? 'active' : ''}
                    >
                      <Box
                        paddingY="4"
                        paddingX="6"
                        textAlign="center"
                        cursor="pointer"
                        onClick={() => {
                          mainSwiper?.slideTo(i);
                        }}
                        tracking={{
                          dataTrack: dataTrack,
                          dataTrackSection: 'recommended-parts',
                          dataTrackText: o.title,
                        }}
                        test={{
                          dataTest: `${dataTest}_tab_${i}`,
                        }}
                        css={{ minWidth: '$size-32' }}
                      >
                        <Text
                          as="h3"
                          size="text-body-2"
                          font="bold"
                          wrap
                          dangerouslySetInnerHTML={{
                            __html: o.title || '',
                          }}
                          data-test={`${dataTest}_tab_title_${i}`}
                        />
                      </Box>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </SlideContainer>
            </Container>
          </Box>
          <Box
            test={{
              dataTest: `${dataTest}_parts`,
            }}
          >
            <Container>
              <Swiper
                modules={[Controller, Keyboard]}
                keyboard={{
                  enabled: true,
                  onlyInViewport: true,
                }}
                speed={1200}
                slidesPerView={1}
                spaceBetween={24}
                autoHeight
                touchReleaseOnEdges={true}
                centeredSlides={false}
                centeredSlidesBounds={false}
                controller={{ control: thumbsSwiper }}
                onSwiper={setMainSwiper}
                onSlideChange={onMainSwiperChange}
              >
                {groups.map((o, i) => (
                  <SwiperSlide key={i}>
                    <Box
                      test={{
                        dataTest: `${dataTest}_parts_group_${i}`,
                      }}
                    >
                      <Grid
                        columnGap="6"
                        rowGap={{ '@initial': '6', '@md': '10' }}
                        css={{
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          '@md': { gridTemplateColumns: 'repeat(3, 1fr)' },
                          '@xl': {
                            gridTemplateColumns: 'repeat(4, 1fr)',
                          },
                        }}
                      >
                        {o.items.map((item, j) => (
                          <PartsItem
                            key={j}
                            index={j}
                            part={item}
                            dataTest={`${dataTest}_parts_item_${j}`}
                            cardUrl={cardUrl}
                          />
                        ))}
                      </Grid>
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Container>
          </Box>
        </>
      )}
    </Section>
  );
};

RecommendedParts.displayName = 'RecommendedParts';
